import { useEffect, useState } from 'react';
import {
  DEFAULT_OPENING_HOURS,
  formatOpeningHoursHuman,
  formatOpeningHoursToOSM,
  from24HourTime,
  isValidHoursRange,
  OPENING_HOURS_HOUR_OPTIONS,
  OPENING_HOURS_MINUTE_OPTIONS,
  parseOpeningHoursFromOSM,
  to24HourTime,
  type AmPmPeriod,
  type OpeningHoursData,
} from '../../utils/openingHours';

interface OpeningHoursEditorProps {
  value: string;
  onChange: (osmString: string) => void;
  /** Show the OSM string preview below the editor. */
  showPreview?: boolean;
}

const selectClass =
  'text-xs px-2 py-2 rounded-xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-all';

function AmPmTimePicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const { hour, minute, period } = from24HourTime(value);

  const update = (nextHour: number, nextMinute: string, nextPeriod: AmPmPeriod) => {
    onChange(to24HourTime(nextHour, nextMinute, nextPeriod));
  };

  return (
    <div className="flex items-center gap-1" role="group" aria-label={label}>
      <select
        value={hour}
        onChange={(e) => update(Number.parseInt(e.target.value, 10), minute, period)}
        className={selectClass}
        aria-label={`${label} hour`}
      >
        {OPENING_HOURS_HOUR_OPTIONS.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-xs text-gray-400">:</span>
      <select
        value={minute}
        onChange={(e) => update(hour, e.target.value, period)}
        className={selectClass}
        aria-label={`${label} minute`}
      >
        {OPENING_HOURS_MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={(e) => update(hour, minute, e.target.value as AmPmPeriod)}
        className={selectClass}
        aria-label={`${label} AM or PM`}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

function HoursRangeRow({
  dayLabel,
  enabled,
  onEnabledChange,
  open,
  close,
  onOpenChange,
  onCloseChange,
}: {
  dayLabel: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  open: string;
  close: string;
  onOpenChange: (value: string) => void;
  onCloseChange: (value: string) => void;
}) {
  const invalid = enabled && !isValidHoursRange(open, close);

  return (
    <div className="px-3 py-2.5 rounded-xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-600 space-y-2">
      <div className="flex items-center gap-2.5 flex-wrap">
        <label className="flex items-center gap-1.5 min-w-[100px]">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
          />
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{dayLabel}</span>
        </label>
        {enabled && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <AmPmTimePicker value={open} onChange={onOpenChange} label={`${dayLabel} opening time`} />
            <span className="text-[10px] text-gray-400 font-medium">to</span>
            <AmPmTimePicker value={close} onChange={onCloseChange} label={`${dayLabel} closing time`} />
          </div>
        )}
      </div>
      {invalid && (
        <p className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-lg px-2.5 py-1.5">
          Closing time must be after opening time (e.g. 5:00 AM to 8:00 AM, not 8:00 AM to 5:00 AM).
        </p>
      )}
    </div>
  );
}

function guardedOpenChange(_open: string, close: string, nextOpen: string): string | null {
  if (close && nextOpen >= close) return null;
  return nextOpen;
}

function guardedCloseChange(open: string, _close: string, nextClose: string): string | null {
  if (open && nextClose <= open) return null;
  return nextClose;
}

export function OpeningHoursEditor({ value, onChange, showPreview = true }: OpeningHoursEditorProps) {
  const [data, setData] = useState<OpeningHoursData>(() => parseOpeningHoursFromOSM(value));

  useEffect(() => {
    setData(parseOpeningHoursFromOSM(value));
  }, [value]);

  const update = (next: OpeningHoursData) => {
    setData(next);
    onChange(formatOpeningHoursToOSM(next));
  };

  const preview = formatOpeningHoursToOSM(data);
  const humanPreview = formatOpeningHoursHuman(data);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-neutral-400">
        Times use AM/PM (Ghana local time). Closing time must be after opening time.
      </p>

      <label className="inline-flex items-center gap-2.5 cursor-pointer px-3 py-2 rounded-xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-600 transition-all hover:border-primary-300 dark:hover:border-primary-700">
        <input
          type="checkbox"
          checked={data.is24_7}
          onChange={(e) => update({ ...data, is24_7: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Open 24/7</span>
      </label>

      {!data.is24_7 && (
        <div className="space-y-2">
          <HoursRangeRow
            dayLabel="Mon – Fri"
            enabled={data.weekdayEnabled}
            onEnabledChange={(weekdayEnabled) => update({ ...data, weekdayEnabled })}
            open={data.weekdayOpen}
            close={data.weekdayClose}
            onOpenChange={(weekdayOpen) => {
              const next = guardedOpenChange(data.weekdayOpen, data.weekdayClose, weekdayOpen);
              if (next) update({ ...data, weekdayOpen: next });
            }}
            onCloseChange={(weekdayClose) => {
              const next = guardedCloseChange(data.weekdayOpen, data.weekdayClose, weekdayClose);
              if (next) update({ ...data, weekdayClose: next });
            }}
          />
          <HoursRangeRow
            dayLabel="Saturday"
            enabled={data.saturdayEnabled}
            onEnabledChange={(saturdayEnabled) => update({ ...data, saturdayEnabled })}
            open={data.saturdayOpen}
            close={data.saturdayClose}
            onOpenChange={(saturdayOpen) => {
              const next = guardedOpenChange(data.saturdayOpen, data.saturdayClose, saturdayOpen);
              if (next) update({ ...data, saturdayOpen: next });
            }}
            onCloseChange={(saturdayClose) => {
              const next = guardedCloseChange(data.saturdayOpen, data.saturdayClose, saturdayClose);
              if (next) update({ ...data, saturdayClose: next });
            }}
          />
          <HoursRangeRow
            dayLabel="Sunday"
            enabled={data.sundayEnabled}
            onEnabledChange={(sundayEnabled) => update({ ...data, sundayEnabled })}
            open={data.sundayOpen}
            close={data.sundayClose}
            onOpenChange={(sundayOpen) => {
              const next = guardedOpenChange(data.sundayOpen, data.sundayClose, sundayOpen);
              if (next) update({ ...data, sundayOpen: next });
            }}
            onCloseChange={(sundayClose) => {
              const next = guardedCloseChange(data.sundayOpen, data.sundayClose, sundayClose);
              if (next) update({ ...data, sundayClose: next });
            }}
          />
        </div>
      )}

      {showPreview && preview && (
        <div className="pl-1 space-y-0.5">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{humanPreview}</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">Stored as: {preview}</p>
        </div>
      )}

      {!data.is24_7 && !preview && (
        <p className="text-xs text-gray-500 dark:text-neutral-500">
          Leave all days unchecked if opening hours are unknown.
        </p>
      )}
    </div>
  );
}

export { DEFAULT_OPENING_HOURS };
